import React, { useMemo } from "react";

import { ANNOTATION_COLORS, ANNOTATION_OPACITY } from "@perceptron/constants/colors";
import { Point, AnnotationSource } from "@perceptron/core/types";
import { normalizedToPercentage, average, COORDINATE_SPACE } from "@perceptron/utils/math";
import { decode } from "he";

import { useAnnotationInteractions } from "@demo/hooks/annotations/useAnnotationInteractions";

import { BasePointAnnotation } from "./BasePointAnnotation";

interface PolygonProps {
  polygon: {
    id: string;
    label?: string;
    coordinates: Point[];
    source: AnnotationSource;
  };
}

export const Polygon = React.memo(function Polygon({ polygon }: PolygonProps) {
  const { isHighlighted, isDimmed, isCollectionHighlighted, clickedAnnotationId, highlightedAnnotationIds } =
    useAnnotationInteractions();

  // Calculate the bounding box of the polygon
  const boundingBox = useMemo(() => {
    const xCoords = polygon.coordinates.map((p) => p.x);
    const yCoords = polygon.coordinates.map((p) => p.y);
    const minX = Math.min(...xCoords);
    const maxX = Math.max(...xCoords);
    const minY = Math.min(...yCoords);
    const maxY = Math.max(...yCoords);
    const width = maxX - minX;
    const height = maxY - minY;
    const avgDimension = average(width, height) / COORDINATE_SPACE;
    return { minX, maxX, minY, maxY, width, height, avgDimension };
  }, [polygon.coordinates]);

  const avgDimension = boundingBox.avgDimension;
  // Scale stroke width based on polygon size (0.15% to 0.5% of average dimension)
  const baseStrokeWidth = Math.max(0.15, Math.min(0.5, avgDimension * 2));

  const getPolygonStyle = () => {
    if (isHighlighted(polygon.id) || isCollectionHighlighted([polygon.id])) {
      return {
        fill: `rgba(${ANNOTATION_COLORS.PRIMARY_RGB}, 0)`,
        stroke: ANNOTATION_COLORS.PRIMARY,
        strokeWidth: baseStrokeWidth * 1.5,
        filter: `drop-shadow(0 0 8px rgba(${ANNOTATION_COLORS.PRIMARY_RGB}, ${ANNOTATION_OPACITY.GLOW}))`,
      };
    }
    if (isDimmed(polygon.id)) {
      return {
        fill: `rgba(${ANNOTATION_COLORS.PRIMARY_RGB}, ${ANNOTATION_OPACITY.FILL_DIMMED})`,
        stroke: `rgba(${ANNOTATION_COLORS.PRIMARY_RGB}, ${ANNOTATION_OPACITY.STROKE_DIMMED})`,
        strokeWidth: baseStrokeWidth * 0.7,
      };
    }
    return {
      fill: `rgba(${ANNOTATION_COLORS.PRIMARY_RGB}, ${ANNOTATION_OPACITY.FILL_DEFAULT})`,
      stroke: ANNOTATION_COLORS.PRIMARY,
      strokeWidth: baseStrokeWidth,
    };
  };

  // Convert coordinates to percentage-based points for SVG polygon
  const points = polygon.coordinates
    .map((point) => `${normalizedToPercentage(point.x)},${normalizedToPercentage(point.y)}`)
    .join(" ");

  const isClicked = clickedAnnotationId === polygon.id;
  // Only show label when this polygon is the single highlighted item (not via collection), matching BoundingBox behavior
  const isSinglePointHighlighted =
    isHighlighted(polygon.id) && (highlightedAnnotationIds?.length === 1 || !highlightedAnnotationIds);
  const shouldShowLabel = isSinglePointHighlighted || isClicked;

  // Check if label would overflow at top (polygon near top of image)
  const labelOverflowsTop = boundingBox.minY < 40;

  // Position label above or below the polygon's bounding box based on overflow
  const labelPosition = {
    left: `${normalizedToPercentage((boundingBox.minX + boundingBox.maxX) / 2)}%`,
    top: labelOverflowsTop
      ? `${normalizedToPercentage(boundingBox.maxY)}%`
      : `${normalizedToPercentage(boundingBox.minY)}%`,
  };

  // Adjust transform based on whether label is above or below
  const labelTransform = labelOverflowsTop
    ? "translateX(-50%) translateY(0.5rem)"
    : "translateX(-50%) translateY(-100%) translateY(-0.5rem)";

  return (
    <BasePointAnnotation
      className="pointer-events-none absolute inset-0"
      id={polygon.id}
      showLabel={false}
      source={polygon.source}
    >
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full"
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
      >
        <polygon
          points={points}
          {...getPolygonStyle()}
          className="pointer-events-auto cursor-pointer transition-all duration-200"
        />
      </svg>
      {shouldShowLabel && polygon.label && (
        <div
          className="absolute z-[1000] whitespace-nowrap rounded-sm px-2 py-0.5 text-xs text-white"
          style={{
            ...labelPosition,
            transform: labelTransform,
            backgroundColor: ANNOTATION_COLORS.PRIMARY,
          }}
        >
          {decode(polygon.label)}
        </div>
      )}
    </BasePointAnnotation>
  );
});
